import { useQuery } from '@tanstack/react-query';
import { storefrontApi, type ProductQuery } from '@/services/api/storefront';
import { useZoneSlug } from '@/stores/zone.store';

/**
 * Sunucu verisi TAMAMEN TanStack Query'de yaşar; Zustand'a kopyalanmaz.
 * Bölge seçimi her anahtarın parçasıdır — bölge değişince tüm teslimat
 * bağımlı veri kendiliğinden tazelenir.
 */

const MINUTE = 60_000;

export function useHome() {
  const zone = useZoneSlug();

  return useQuery({
    queryKey: ['home', zone ?? 'default'],
    queryFn: () => storefrontApi.home(zone),
    // Geri sayım ve "bugün teslim" bilgisi zamana bağlı: kısa taze süre.
    staleTime: 2 * MINUTE,
  });
}

export function useCategories() {
  return useQuery({
    queryKey: ['categories'],
    queryFn: storefrontApi.categories,
    staleTime: 30 * MINUTE,
  });
}

export function useCategory(slug: string | undefined) {
  return useQuery({
    queryKey: ['category', slug],
    queryFn: () => storefrontApi.category(slug as string),
    enabled: Boolean(slug),
    staleTime: 30 * MINUTE,
  });
}

export function useCollection(slug: string | undefined) {
  return useQuery({
    queryKey: ['collection', slug],
    queryFn: () => storefrontApi.collection(slug as string),
    enabled: Boolean(slug),
    staleTime: 30 * MINUTE,
  });
}

export function useProducts(query: ProductQuery) {
  const zone = useZoneSlug();

  return useQuery({
    queryKey: ['products', { ...query, zone: zone ?? 'default' }],
    queryFn: () => storefrontApi.products({ ...query, zone }),
    staleTime: 2 * MINUTE,
    // Sayfa değişiminde liste boşalmasın; eski sonuç yerinde kalsın.
    placeholderData: (previous) => previous,
  });
}

export function useProduct(slug: string | undefined) {
  const zone = useZoneSlug();

  return useQuery({
    queryKey: ['product', slug, zone ?? 'default'],
    queryFn: () => storefrontApi.product(slug as string, zone),
    enabled: Boolean(slug),
    staleTime: 2 * MINUTE,
  });
}

export function useSimilarProducts(slug: string | undefined) {
  return useQuery({
    queryKey: ['product', slug, 'similar'],
    queryFn: () => storefrontApi.similar(slug as string),
    enabled: Boolean(slug),
    staleTime: 15 * MINUTE,
  });
}

export function useProductReviews(slug: string | undefined) {
  return useQuery({
    queryKey: ['product', slug, 'reviews'],
    queryFn: () => storefrontApi.reviews(slug as string),
    enabled: Boolean(slug),
    staleTime: 15 * MINUTE,
  });
}

export function useZones() {
  return useQuery({
    queryKey: ['delivery', 'zones'],
    queryFn: storefrontApi.zones,
    staleTime: 60 * MINUTE,
  });
}

/** Ürün detayındaki teslimat kartı bu veriden beslenir. */
export function useAvailability(productSlug?: string) {
  const zone = useZoneSlug();

  return useQuery({
    queryKey: ['delivery', 'availability', zone ?? 'default', productSlug ?? 'store'],
    queryFn: () =>
      storefrontApi.availability({
        zone,
        product: productSlug ? [productSlug] : undefined,
      }),
    staleTime: MINUTE,
  });
}

/**
 * Yerel arama sayfaları.
 *
 * İçerik (hangi bölgeye hizmet veriliyor, ücret, kesim saati) sunucudan
 * gelir; istemci bu kararları tekrar etmez. Teslimat verisi gün içinde
 * nadiren değiştiği için uzun taze süre yeterlidir.
 */
export function useLocalSeoPages() {
  return useQuery({
    queryKey: ['seo', 'local'],
    queryFn: storefrontApi.localPages,
    staleTime: 30 * MINUTE,
  });
}

export function useLocalSeoPage(slug: string | undefined) {
  return useQuery({
    queryKey: ['seo', 'local', slug],
    queryFn: () => storefrontApi.localPage(slug as string),
    enabled: Boolean(slug),
    staleTime: 30 * MINUTE,
    retry: false,
  });
}
